<template>
    <div class="review-reply"
         :id="`comment-${reply.id}`"
         :data-reply-id="reply.id"
         :data-depth="level">
        <div class="py-4">
            <!-- Header -->
            <div class="mb-2 flex items-center justify-between">
                <div class="flex min-w-0 items-center space-x-2 sm:space-x-3">
                    <template v-if="!reply.is_guest && reply.user && reply.user.username">
                        <a :href="profileUrl(reply.user.username)" class="group flex items-center space-x-2 sm:space-x-3" @click.stop>
                            <user-avatar
                                :src="reply.user.avatar_display || ''"
                                :name="reply.user.name"
                                size="xs"
                                class="bg-gradient-to-br from-blue-500 to-green-500 text-xs text-white transition-transform duration-200 group-hover:scale-110 sm:!h-8 sm:!w-8 sm:!basis-8"
                            />
                            <div class="min-w-0 flex-1">
                                <div class="truncate text-sm font-bold text-foxy-text transition-colors group-hover:text-foxy-action">
                                    {{ reply.user?.name || 'Користувач' }}
                                </div>
                                <div class="truncate text-xs text-foxy-text-secondary transition-colors group-hover:text-foxy-action">
                                    {{ '@' + reply.user.username }} · {{ formatDate(reply.created_at) }}
                                </div>
                            </div>
                        </a>
                    </template>
                    <template v-else>
                        <user-avatar
                            :src="reply.user && reply.user.avatar_display ? reply.user.avatar_display : ''"
                            :name="reply.user ? reply.user.name : ''"
                            :guest="reply.is_guest"
                            size="xs"
                            class="bg-gradient-to-br from-blue-500 to-green-500 text-xs text-white sm:!h-8 sm:!w-8 sm:!basis-8"
                        />
                        <div class="min-w-0 flex-1">
                            <div class="truncate text-sm font-bold text-foxy-text">
                                {{ reply.is_guest ? 'Гість' : reply.user?.name || 'Користувач' }}
                            </div>
                            <div class="truncate text-xs text-foxy-text-secondary">
                                <template v-if="reply.user && reply.user.username">{{ '@' + reply.user.username }} · </template
                                >{{ formatDate(reply.created_at) }}
                            </div>
                        </div>
                    </template>
                </div>
            </div>

            <!-- Reply Content -->
            <div v-if="!isEditing">
                <!-- Blocked Content -->
                <div v-if="reply.status === 'blocked'" class="my-3">
                    <div class="border-2 border-red-300 dark:border-red-700 rounded-lg bg-red-50/50 dark:bg-red-900/20 p-4">
                        <div class="relative overflow-hidden rounded-md mb-3">
                            <p class="review-reply-content text-foxy-text leading-relaxed text-sm break-words blur-sm filter"
                               style="word-break: break-word; overflow-wrap: break-word; hyphens: auto; -webkit-hyphens: auto; -ms-hyphens: auto;">
                                {{ reply.content }}
                            </p>
                        </div>
                        <div class="flex items-start gap-3 pt-2 border-t border-red-200 dark:border-red-800">
                            <div class="flex-shrink-0 mt-0.5">
                                <i class="fas fa-exclamation-triangle text-red-500 dark:text-red-400 text-lg"></i>
                            </div>
                            <div class="flex-1 min-w-0">
                                <p class="text-xs text-red-700 dark:text-red-300 font-medium mb-1">
                                    Контент заблоковано адміністрацією сайту
                                </p>
                                <p v-if="reply.moderation_reason" class="text-xs text-red-600 dark:text-red-400 mt-1">
                                    Причина: {{ reply.moderation_reason }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Normal Content -->
                <div v-else
                     class="review-reply-content text-foxy-text leading-relaxed text-sm break-words"
                     style="word-break: break-word; overflow-wrap: break-word; hyphens: auto; -webkit-hyphens: auto; -ms-hyphens: auto;"
                     v-html="formatContent(reply.content)">
                </div>
            </div>

            <!-- Edit Form -->
            <div v-if="isEditing" class="mb-3 sm:mb-4">
                <textarea v-model="editContent"
                          ref="editContentTextarea"
                          class="foxy-textarea w-full !p-3 sm:!p-4 !rounded-xl resize-none transition-colors text-sm sm:text-base"
                          rows="3"></textarea>
                <div class="flex justify-end space-x-2 mt-3">
                    <button @click="cancelEdit"
                            class="px-3 py-2 sm:px-4 bg-foxy-elevated text-foxy-text rounded-lg hover:bg-foxy-border/70 transition-colors text-sm">
                        Скасувати
                    </button>
                    <button @click="saveEdit"
                            :disabled="isSaving"
                            class="px-3 py-2 sm:px-4 bg-foxy-action text-white rounded-lg hover:bg-foxy-action/90 transition-colors text-sm">
                        {{ isSaving ? 'Збереження...' : 'Зберегти' }}
                    </button>
                </div>
            </div>

            <!-- Actions -->
            <div v-if="isAuthenticated" class="flex items-center justify-between flex-wrap gap-2">
                <div class="flex items-center space-x-3 sm:space-x-4">
                    <!-- Toggle Button -->
                    <button v-if="reply.replies && reply.replies.length > 0"
                            @click="toggleReplies"
                            class="toggle-button flex items-center space-x-1 sm:space-x-2 text-foxy-text-secondary hover:text-foxy-text text-xs sm:text-sm">
                        <i :class="['fas text-xs toggle-icon', repliesExpanded ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
                        <span class="font-medium">
                            <span>{{ repliesExpanded ? 'Згорнути' : 'Відповіді' }}</span>
                            <span class="text-foxy-text-secondary">({{ reply.replies.length }})</span>
                        </span>
                    </button>

                    <!-- Like Button -->
                    <button @click="toggleLike"
                            class="flex items-center py-1 rounded-lg transition-colors">
                        <i :class="['fas fa-heart text-xs sm:text-sm', isLiked ? 'text-red-500 dark:text-red-400' : 'text-foxy-text-secondary']"></i>
                        <span class="ml-1 text-foxy-text-secondary text-xs sm:text-sm">
                            {{ likesCount }}
                        </span>
                    </button>
                </div>

                <div class="flex items-center space-x-2">
                    <!-- Reply Button -->
                    <button @click="showReplyForm = !showReplyForm"
                            class="flex items-center space-x-1 text-foxy-text-secondary hover:text-foxy-action transition-colors text-xs sm:text-sm">
                        <i class="fas fa-reply text-xs"></i>
                        <span>Відповісти</span>
                    </button>

                    <!-- Edit Button (for owner) -->
                    <button v-if="canEdit" @click="startEdit"
                            class="text-foxy-text-secondary hover:text-foxy-action transition-colors p-1">
                        <i class="fas fa-edit text-xs"></i>
                    </button>

                    <!-- Delete Button (for owner) -->
                    <button v-if="canEdit" @click="deleteReply"
                            class="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors p-1">
                        <i class="fas fa-trash text-xs"></i>
                    </button>

                    <!-- Report Button -->
                    <report-button 
                        :reportable-type="'App\\Models\\Review'"
                        :reportable-id="reply.id"
                        :content-preview="getContentPreview()"
                        :content-url="getContentUrl()">
                    </report-button>
                </div>
            </div>

            <!-- Reply Form -->
            <div v-if="showReplyForm" class="mb-3 sm:mb-4 mt-3">
                <div class="relative">
                    <textarea v-model="replyContent"
                              @input="handleReplyInput"
                              @keydown="handleReplyKeydown"
                              ref="replyContentTextarea"
                              class="foxy-textarea w-full !p-3 sm:!p-4 !rounded-xl resize-none transition-colors text-sm sm:text-base"
                              rows="3" 
                              placeholder="Напишіть відповідь…"></textarea>
                    
                    <!-- Mention Dropdown -->
                    <div v-if="showMentionDropdown && mentionUsers.length > 0" 
                         class="absolute z-[9999] bg-foxy-surface border border-foxy-border rounded-xl shadow-xl max-h-60 overflow-y-auto mt-1"
                         :style="{ 
                             top: '100%',
                             left: '0px', 
                             minWidth: '250px',
                             maxWidth: '300px'
                         }">
                    <div v-for="(user, index) in mentionUsers" 
                         :key="user.id"
                         @click="insertMention(user.username)"
                         @mouseenter="selectedMentionIndex = index"
                         :class="[
                             'px-4 py-3 flex items-center space-x-3 cursor-pointer transition-colors',
                             selectedMentionIndex === index 
                                 ? 'bg-foxy-action/10 text-foxy-action'
                                 : 'hover:bg-foxy-elevated'
                         ]">
                        <user-avatar
                            :src="user.avatar_display || ''"
                            :name="user.name"
                            size="sm"
                        />
                        <div class="flex-1 min-w-0">
                            <div class="text-sm font-medium text-foxy-text truncate">
                                {{ user.name }}
                            </div>
                            <div class="text-xs text-foxy-text-secondary truncate">
                                @{{ user.username }}
                            </div>
                        </div>
                    </div>
                </div>
                </div>
                
                <div class="flex justify-end space-x-2 mt-3">
                    <button @click="cancelReply"
                            class="px-3 py-2 sm:px-4 bg-foxy-elevated text-foxy-text rounded-lg hover:bg-foxy-border/70 transition-colors text-sm">
                        Скасувати
                    </button>
                    <button @click="submitReply"
                            :disabled="isSubmitting || !replyContent.trim()"
                            class="px-3 py-2 sm:px-4 bg-foxy-action text-white rounded-lg hover:bg-foxy-action/90 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                        {{ isSubmitting ? 'Надсилаємо…' : 'Надіслати' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Nested Replies -->
        <div v-if="reply.replies && reply.replies.length > 0 && repliesExpanded" class="mt-4">
            <div class="space-y-4 replies-container">
                <review-reply v-for="nestedReply in reply.replies"
                              :key="nestedReply.id"
                              :reply="nestedReply"
                              :level="level + 1"
                              :book-slug="bookSlug"
                              :review-id="reviewId"
                              :current-user-id="currentUserId"
                              :is-moderator="isModerator"
                              :target-comment-id="targetCommentId"
                              @reply-added="handleReplyAdded"
                              @reply-updated="handleReplyUpdated"
                              @reply-deleted="handleReplyDeleted"
                              @like-toggled="handleLikeToggled"
                              @show-notification="showNotification" />
            </div>
        </div>

    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'ReviewReply',
    props: {
        reply: {
            type: Object,
            required: true
        },
        level: {
            type: Number,
            default: 0
        },
        bookSlug: {
            type: String,
            required: true
        },
        reviewId: {
            type: Number,
            required: true
        },
        currentUserId: {
            type: Number,
            default: null
        },
        isModerator: {
            type: Boolean,
            default: false
        },
        targetCommentId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            repliesExpanded: false,
            isLiked: this.reply.is_liked_by_current_user || false,
            likesCount: this.reply.likes_count || 0,
            isEditing: false,
            editContent: this.reply.content,
            isSaving: false,
            showReplyForm: false,
            replyContent: '',
            isSubmitting: false,
            // Mention autocomplete
            mentionUsers: [],
            showMentionDropdown: false,
            mentionPosition: { top: 0, left: 0 },
            mentionQuery: '',
            selectedMentionIndex: -1,
            mentionStartPos: -1,
            mentionSearchTimeout: null,
        };
    },
    computed: {
        hasReplies() {
            return this.reply.replies && this.reply.replies.length > 0;
        },
        isAuthenticated() {
            return this.currentUserId !== null;
        },
        canEdit() {
            return this.isAuthenticated && (this.reply.user_id === this.currentUserId || this.isModerator);
        },
        itemLabel() {
            return this.level > 0 ? 'Відповідь' : 'Коментар';
        },
        itemNoun() {
            return this.level > 0 ? 'відповіді' : 'коментаря';
        },
        itemAccusative() {
            return this.level > 0 ? 'цю відповідь' : 'цей коментар';
        }
    },
    watch: {
        targetCommentId: {
            immediate: true,
            handler(targetId) {
                if (targetId && Number(this.reply.id) !== targetId && this.replyContainsTarget(this.reply, targetId)) {
                    this.repliesExpanded = true;
                }
            }
        }
    },
    methods: {
        replyContainsTarget(reply, targetId) {
            if (Number(reply.id) === targetId) {
                return true;
            }

            return (reply.replies || []).some(child => this.replyContainsTarget(child, targetId));
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            const now = new Date();
            const diff = Math.floor((now - date) / 1000); // seconds

            if (diff < 60) return 'щойно';
            if (diff < 3600) return `${Math.floor(diff / 60)} хв тому`;
            if (diff < 86400) return `${Math.floor(diff / 3600)} год тому`;
            if (diff < 604800) return `${Math.floor(diff / 86400)} дн тому`;
            
            return date.toLocaleDateString('uk-UA', {
                day: 'numeric',
                month: 'short',
                year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
            });
        },
        toggleReplies() {
            this.repliesExpanded = !this.repliesExpanded;
        },
        async toggleLike() {
            if (!this.isAuthenticated) {
                this.$emit('show-notification', 'Будь ласка, увійдіть, щоб поставити лайк.', 'error');
                return;
            }
            try {
                const response = await axios.post(`/books/${this.bookSlug}/reviews/${this.reply.id}/like`);
                if (response.data.success) {
                    this.isLiked = response.data.is_liked;
                    this.likesCount = response.data.likes_count;
                    this.$emit('like-toggled', { 
                        replyId: this.reply.id, 
                        isLiked: this.isLiked, 
                        likesCount: this.likesCount 
                    });
                }
            } catch (error) {
                console.error('Error toggling like:', error);
                this.$emit('show-notification', 'Помилка при зміні лайка.', 'error');
            }
        },
        startEdit() {
            this.isEditing = true;
            this.editContent = this.reply.content;
            this.$nextTick(() => {
                this.$refs.editContentTextarea.focus();
            });
        },
        cancelEdit() {
            this.isEditing = false;
            this.editContent = this.reply.content;
        },
        async saveEdit() {
            if (!this.editContent.trim()) {
                this.$emit('show-notification', `Будь ласка, введіть текст ${this.itemNoun}.`, 'error');
                return;
            }
            this.isSaving = true;
            try {
                const response = await axios.post(`/books/${this.bookSlug}/reviews/${this.reply.id}/update`, {
                    content: this.editContent
                });
                if (response.data.success) {
                    this.reply.content = response.data.content;
                    this.isEditing = false;
                    this.$emit('show-notification', `${this.itemLabel} оновлено.`, 'success');
                    this.$emit('reply-updated', { ...this.reply, content: response.data.content });
                } else {
                    this.$emit('show-notification', response.data.message || `Не вдалося оновити ${this.itemNoun}.`, 'error');
                }
            } catch (error) {
                console.error('Error saving edit:', error);
                this.$emit('show-notification', error.response?.data?.message || `Не вдалося оновити ${this.itemNoun}. Спробуйте ще раз.`, 'error');
            } finally {
                this.isSaving = false;
            }
        },
        async deleteReply() {
            const confirmed = await confirm(`Ви впевнені, що хочете видалити ${this.itemAccusative}?`, 'Підтвердження', 'warning');
            if (!confirmed) return;
            try {
                const response = await axios.delete(`/books/${this.bookSlug}/reviews/${this.reply.id}/delete`);
                if (response.data.success) {
                    this.$emit('show-notification', `${this.itemLabel} видалено.`, 'success');
                    this.$emit('reply-deleted', this.reply.id);
                } else {
                    this.$emit('show-notification', response.data.message || `Не вдалося видалити ${this.itemNoun}.`, 'error');
                }
            } catch (error) {
                console.error('Error deleting reply:', error);
                this.$emit('show-notification', error.response?.data?.message || `Не вдалося видалити ${this.itemNoun}. Спробуйте ще раз.`, 'error');
            }
        },
        toggleReplyForm() {
            this.showReplyForm = !this.showReplyForm;
            if (!this.showReplyForm) {
                this.replyContent = '';
            } else {
                this.$nextTick(() => {
                    this.$refs.replyContentTextarea.focus();
                });
            }
        },
        handleReplyInput(event) {
            this.$nextTick(() => {
                const textarea = event.target;
                if (!textarea) return;
                
                const cursorPos = textarea.selectionStart;
                const currentValue = textarea.value || this.replyContent;
                const textBeforeCursor = currentValue.substring(0, cursorPos);
                
                const atIndex = textBeforeCursor.lastIndexOf('@');
                
                if (atIndex === -1) {
                    this.hideMentionDropdown();
                    return;
                }
                
                const textAfterAt = textBeforeCursor.substring(atIndex + 1);
                // Если после @ есть пробел или перенос строки, скрываем dropdown
                if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
                    this.hideMentionDropdown();
                    return;
                }
                
                // Показываем dropdown сразу после @, без требования минимального количества символов
                const query = textAfterAt.trim();
                this.mentionQuery = query;
                this.mentionStartPos = atIndex;
                this.searchUsers(query);
                this.calculateMentionPosition(textarea);
            });
        },
        
        async searchUsers(query) {
            if (this.mentionSearchTimeout) {
                clearTimeout(this.mentionSearchTimeout);
            }
            
            // Уменьшаем задержку для более быстрой реакции
            this.mentionSearchTimeout = setTimeout(async () => {
                try {
                    const url = `/api/users/search${query ? '?q=' + encodeURIComponent(query) : ''}`;
                    const response = await fetch(url);
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    const data = await response.json();
                    this.mentionUsers = data.users || [];
                    this.showMentionDropdown = this.mentionUsers.length > 0;
                    this.selectedMentionIndex = -1;
                    
                    // Recalculate position after dropdown appears
                    if (this.showMentionDropdown) {
                        this.$nextTick(() => {
                            const textarea = this.$refs.replyContentTextarea;
                            if (textarea) {
                                this.calculateMentionPosition(textarea);
                            }
                        });
                    }
                } catch (error) {
                    console.error('Error searching users:', error);
                    this.mentionUsers = [];
                    this.showMentionDropdown = false;
                }
            }, 200);
        },
        
        calculateMentionPosition(textarea) {
            // Позиционирование не нужно, используется top: 100% для размещения под textarea
            // Метод оставлен для совместимости, но не используется
        },
        
        insertMention(username) {
            const textarea = this.$refs.replyContentTextarea;
            if (!textarea) return;
            
            const cursorPos = textarea.selectionStart;
            const currentValue = textarea.value || this.replyContent;
            const textBeforeCursor = currentValue.substring(0, cursorPos);
            const textAfterCursor = currentValue.substring(cursorPos);
            
            const atIndex = textBeforeCursor.lastIndexOf('@');
            if (atIndex === -1) return;
            
            const beforeMention = currentValue.substring(0, atIndex);
            const afterMention = textAfterCursor;
            
            this.replyContent = beforeMention + '@' + username + ' ' + afterMention;
            
            this.$nextTick(() => {
                const newCursorPos = atIndex + username.length + 2;
                textarea.setSelectionRange(newCursorPos, newCursorPos);
                textarea.focus();
            });
            
            this.hideMentionDropdown();
        },
        
        handleReplyKeydown(event) {
            if (!this.showMentionDropdown || this.mentionUsers.length === 0) {
                return;
            }
            
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.selectedMentionIndex = Math.min(
                    this.selectedMentionIndex + 1,
                    this.mentionUsers.length - 1
                );
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.selectedMentionIndex = Math.max(this.selectedMentionIndex - 1, -1);
            } else if (event.key === 'Enter' || event.key === 'Tab') {
                if (this.selectedMentionIndex >= 0 && this.selectedMentionIndex < this.mentionUsers.length) {
                    event.preventDefault();
                    const selectedUser = this.mentionUsers[this.selectedMentionIndex];
                    this.insertMention(selectedUser.username);
                }
            } else if (event.key === 'Escape') {
                this.hideMentionDropdown();
            }
        },
        
        hideMentionDropdown() {
            this.showMentionDropdown = false;
            this.mentionUsers = [];
            this.selectedMentionIndex = -1;
            this.mentionQuery = '';
            this.mentionStartPos = -1;
            if (this.mentionSearchTimeout) {
                clearTimeout(this.mentionSearchTimeout);
                this.mentionSearchTimeout = null;
            }
        },
        
        cancelReply() {
            this.showReplyForm = false;
            this.replyContent = '';
            this.hideMentionDropdown();
        },
        async submitReply() {
            if (!this.replyContent.trim()) {
                this.$emit('show-notification', 'Будь ласка, введіть текст відповіді.', 'error');
                return;
            }
            this.hideMentionDropdown();
            this.isSubmitting = true;
            try {
                const response = await axios.post(`/books/${this.bookSlug}/reviews/${this.reviewId}/replies`, {
                    content: this.replyContent,
                    parent_id: this.reply.id
                });
                if (response.data.success) {
                    this.replyContent = '';
                    this.showReplyForm = false;
                    this.$emit('reply-added', response.data.reply);
                    this.$emit('show-notification', 'Відповідь додано.', 'success');
                } else {
                    this.$emit('show-notification', response.data.message || 'Не вдалося надіслати відповідь.', 'error');
                }
            } catch (error) {
                console.error('Error submitting reply:', error);
                this.$emit('show-notification', error.response?.data?.message || 'Не вдалося надіслати відповідь. Спробуйте ще раз.', 'error');
            } finally {
                this.isSubmitting = false;
            }
        },
        handleReplyAdded(newReply) {
            // Проверяем, является ли новый ответ прямым дочерним элементом
            if (newReply.parent_id === this.reply.id) {
                // Только если это прямой ребенок, добавляем к текущему компоненту
                if (!this.reply.replies) {
                    this.$set(this.reply, 'replies', []);
                }
                this.reply.replies.unshift(newReply);
                this.$set(this.reply, 'replies_count', (this.reply.replies_count || 0) + 1);
            }
            // Всегда пробрасываем событие выше для обновления общего счетчика
            this.$emit('reply-added', newReply);
        },
        handleReplyUpdated(updatedReply) {
            const index = this.reply.replies.findIndex(r => r.id === updatedReply.id);
            if (index !== -1) {
                this.$set(this.reply.replies, index, updatedReply);
            }
            this.$emit('reply-updated', updatedReply);
        },
        handleReplyDeleted(deletedReplyId) {
            this.reply.replies = this.reply.replies.filter(r => r.id !== deletedReplyId);
            this.$set(this.reply, 'replies_count', Math.max(0, (this.reply.replies_count || 0) - 1));
            this.$emit('reply-deleted', deletedReplyId);
        },
        handleLikeToggled(payload) {
            this.$emit('like-toggled', payload);
        },
        showNotification(message, type = 'info') {
            // Эмитим событие родителю для единообразия
            this.$emit('show-notification', message, type);
        },

        getContentPreview() {
            // Возвращаем превью контента для жалобы
            if (this.reply.content) {
                const text = this.reply.content;
                return text.substring(0, 100) + (text.length > 100 ? '...' : '');
            }
            return this.level > 0 ? 'Відповідь на коментар' : 'Коментар до рецензії';
        },

        getContentUrl() {
            // Возвращаем URL контента
            return `/books/${this.bookSlug}/reviews/${this.reviewId}#comment-${this.reply.id}`;
        },
        profileUrl(username) {
            return username ? `/users/${username}` : '#';
        },
        
        formatContent(content) {
            if (!content) return '';
            
            // Escape HTML to prevent XSS
            const escaped = content
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
            
            // Replace @username mentions with styled links
            // Supports Cyrillic, Latin, numbers, underscores, and hyphens
            const mentionRegex = /@([a-zA-Zа-яА-ЯёЁіІїЇєЄ0-9_-]+)/gu;
            
            return escaped.replace(mentionRegex, (match, username) => {
                const profileUrl = `/users/${username}`;
                return `<a href="${profileUrl}" class="mention-link text-foxy-action hover:text-foxy-action/80 font-bold transition-colors" target="_blank">@${username}</a>`;
            });
        }
    }
};
</script>

<style scoped>
/* Review Reply Styles */
.review-reply {
    transition: all 0.2s ease-in-out;
}

.review-reply-content {
    white-space: pre-wrap;
}

.review-reply.notification-comment-target {
    background-color: rgb(var(--foxy-action) / 0.12);
    border-radius: 0.75rem;
    outline: 2px solid rgb(var(--foxy-action) / 0.55);
    outline-offset: 0.25rem;
}

@media (prefers-reduced-motion: reduce) {
    .review-reply {
        transition: none;
    }
}

.replies-container {
    transition: all 0.3s ease-in-out;
}

/* Depth Indicators for Visual Hierarchy */
.review-reply[data-depth='1'] {
    border-left: 1px solid rgb(var(--foxy-border));
    padding-left: 1rem;
}

.review-reply[data-depth='2'] {
    border-left: 1px solid rgb(var(--foxy-border));
    padding-left: 1rem;
}

.review-reply[data-depth='3'] {
    border-left: 1px solid rgb(var(--foxy-border));
    padding-left: 1rem;
}

.review-reply[data-depth='4'] {
    border-left: 1px solid rgb(var(--foxy-border));
    padding-left: 1rem;
}

.review-reply[data-depth='5'] {
    border-left: 1px solid rgb(var(--foxy-border));
    padding-left: 1rem;
}

.review-reply[data-depth='6'] {
    border-left: 1px solid rgb(var(--foxy-border));
    padding-left: 1rem;
}

/* Mobile optimizations */
@media (max-width: 640px) {
    .review-reply img {
        width: 1.5rem;
        height: 1.5rem;
    }

    .review-reply[data-depth='1'],
    .review-reply[data-depth='2'],
    .review-reply[data-depth='3'],
    .review-reply[data-depth='4'],
    .review-reply[data-depth='5'] {
        border-left-width: 1px;
        padding-left: 0.5rem;
    }
}

/* Mention link styles */
.mention-link {
    color: rgb(var(--foxy-action));
    font-weight: 700;
    text-decoration: none;
    transition: color 0.2s ease-in-out;
}

.mention-link:hover {
    color: rgb(var(--foxy-action) / 0.8);
}

.dark .mention-link {
    color: rgb(var(--foxy-action));
}

.dark .mention-link:hover {
    color: rgb(var(--foxy-action) / 0.8);
}
</style>
